/**
 * Personal-link token handling.
 *
 * The canonical link is `https://<app>/#t=<64 hex chars>` — the token lives in
 * the URL **fragment**, deliberately.
 *
 * Why the fragment and not `?t=`: browsers never transmit the fragment to the
 * server. A query string would be written into Cloudflare's request logs and any
 * intermediate proxy's logs on every single visit, permanently, in plain text.
 * Since the token is the entire credential, that is a log file full of working
 * passwords. The fragment never leaves the browser, so it cannot be logged
 * server-side at all.
 *
 * `?t=` is still accepted so older links keep working, but links handed out
 * should always use `#t=`. See README.
 *
 * On first open we stash the token and strip it from the address bar, so that:
 *   - the home-screen shortcut keeps working without the token in the URL, and
 *   - the credential stops riding along in a bar that can be screenshotted,
 *     pasted into a group chat, or read over a shoulder.
 *
 * localStorage rather than a cookie: there is no server session to pair with,
 * and it survives the app being closed. It is readable by any script on this
 * origin, which is acceptable because the app is self-contained and loads no
 * third-party code (enforced by the CSP in `public/_headers`).
 */

const STORAGE_KEY = 'ops-task-portal.token'

/** Pull `t` out of the fragment, e.g. `#t=abc` or `#/foo?t=abc`. */
function fromFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  if (raw === '') return null
  // Handles both `t=abc` and `anything?t=abc`.
  const qs = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : raw
  const value = new URLSearchParams(qs).get('t')
  return value && value.trim() !== '' ? value.trim() : null
}

export function captureToken(): string | null {
  const url = new URL(window.location.href)

  const fragmentToken = fromFragment(url.hash)
  const queryToken = url.searchParams.get('t')?.trim() || null
  const incoming = fragmentToken ?? queryToken

  if (incoming) {
    try {
      window.localStorage.setItem(STORAGE_KEY, incoming)
    } catch {
      // Private browsing can refuse writes. The in-memory token still works for
      // this visit; the person just has to reopen their link next time.
    }
    // Scrub both carriers from the address bar.
    url.searchParams.delete('t')
    window.history.replaceState({}, '', url.pathname + url.search)
    return incoming
  }

  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/** Persist a token obtained from the name picker, exactly as a link would. */
export function storeToken(token: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, token)
  } catch {
    // Private browsing can refuse writes; the caller keeps it in memory.
  }
}

export function forgetToken(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing useful to do; the caller is already reloading.
  }
}
